import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  linkedSignal,
  signal,
} from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { of } from "rxjs";
import { AuthService } from "../../services/api/auth/auth.service";
import { EmailsService } from "../../services/api/emails/emails.service";
import { ModalService } from "../../services/modal/modal.service";
import { SnackbarService } from "../../services/snackbar/snackbar.service";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import {
  EmailSubscriptionStatus,
  UserSubscriptionView,
} from "../../models/email-subscription.model";

// TODO: Add a deep link so user can just open the email preferences from the email link
@Component({
  selector: "app-email-preferences",
  imports: [CommonModule],
  templateUrl: "./email-preferences.html",
  styleUrl: "./email-preferences.scss",
})
export class EmailPreferences {
  modalService = inject(ModalService);
  private emailsService = inject(EmailsService);
  private snackbar = inject(SnackbarService);
  private sanitizer = inject(DomSanitizer);
  private authService = inject(AuthService);
  expandedCard = signal(new Set<number>());
  email = this.authService.getEmail();

  constructor() {
    effect(() => {
      if (this.modalService.emailPreferencesModal()) {
        history.pushState({ emailPrefsModal: true }, "");
      }
    });

    const onPopState = () => {
      if (this.modalService.emailPreferencesModal()) {
        this.closeModal({ fromHistory: true });
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && this.modalService.emailPreferencesModal()) {
        this.closeModal();
      }
    };
    window.addEventListener("popstate", onPopState);
    window.addEventListener("keydown", onKeyDown);
    inject(DestroyRef).onDestroy(() => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("keydown", onKeyDown);
    });
  }

  readonly subscriptionsResource = rxResource({
    // Signal read must live in `params` — `stream` is not reactive, so reading
    // the modal signal there would only evaluate once (at component creation,
    // while the modal is closed) and never refetch when it opens.
    params: () => this.modalService.emailPreferencesModal(),
    stream: ({ params: isOpen }) =>
      isOpen ? this.emailsService.getSubscriptions() : of([]),
  });

  readonly editedStatus = linkedSignal<
    UserSubscriptionView[] | undefined,
    Record<number, EmailSubscriptionStatus>
  >({
    source: () => this.subscriptionsResource.value(),
    computation: (newResources) => {
      if (!newResources) {
        return {};
      }
      return Object.fromEntries(
        newResources.map((sub) => [sub.topicId, sub.status]),
      );
    },
  });

  toggleStatus(topicId: number) {
    this.editedStatus.update((current) => ({
      ...current,
      [topicId]:
        current[topicId] === "subscribed" ? "unsubscribed" : "subscribed",
    }));
  }

  isSubscribed(topicId: number): boolean {
    return this.editedStatus()[topicId] === "subscribed";
  }

  readonly hasUnsavedChanges = computed(() => {
    const subs = this.subscriptionsResource.value() ?? [];
    const edited = this.editedStatus();
    return subs.some((s) => edited[s.topicId] !== s.status);
  });

  readonly saving = signal(false);

  save(): void {
    const subs = this.subscriptionsResource.value() ?? [];
    const edited = this.editedStatus();
    const updates = subs
      .filter((s) => edited[s.topicId] !== s.status)
      .map((s) => ({ topicId: s.topicId, status: edited[s.topicId] }));

    if (updates.length === 0) return;

    this.saving.set(true);
    this.emailsService.updateSubscriptions(updates).subscribe({
      next: (updated) => {
        this.subscriptionsResource.value.set(updated);
        this.saving.set(false);
        this.snackbar.show({ message: "Email preferences saved." });
      },
      error: (err) => {
        console.error("Failed to save email preferences", err);
        this.saving.set(false);
        this.snackbar.show({
          message: "Couldn't save email preferences. Please try again.",
        });
      },
    });
  }

  trustSvg(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  async closeModal(opts: { fromHistory?: boolean } = {}): Promise<void> {
    if (this.hasUnsavedChanges()) {
      const result = await this.modalService.openDialogAsync({
        title: "Discard changes?",
        message:
          "You have unsaved changes to your email preferences. Close anyway?",
        variant: "info",
        actions: [
          { label: "Keep editing", action: () => "cancel" },
          { label: "Discard", action: () => "confirm" },
        ],
      });
      if (result !== "confirm") {
        // Back button already popped our synthetic entry — re-push so back still closes.
        if (opts.fromHistory) {
          history.pushState({ emailPrefsModal: true }, "");
        }
        return;
      }
    }
    this.modalService.emailPreferencesModal.set(false);

    // If the history entry is already gone, we shouldn't go back again.
    if (!opts.fromHistory) {
      history.back();
    }
  }

  isExpanded(cardId: number) {
    return this.expandedCard().has(cardId);
  }

  toggleCard(cardId: number): void {
    this.expandedCard.update((set) => {
      const newSet = new Set(set);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  }

  readonly emailStubData: Record<
    string,
    {
      description: string;
      emailIncludeCardInfo: { icon: string; title: string }[];
    }
  > = {
    "weekly-summary": {
      description:
        "A summary of your ET budget, top water use fields, climate updates, and crop overview — delivered every Monday morning, 7 AM Pacific (only if you have claimed fields).",
      emailIncludeCardInfo: [
        {
          icon: walletSvg,
          title: "ET Budget Summary",
        },
        {
          icon: rankingSvg,
          title: "Top 10 Fields",
        },
        {
          icon: weatherSvg,
          title: "Climate Update",
        },
        {
          icon: tractorSvg,
          title: "Crop Acreage Breakdown",
        },
      ],
    },
  } as const;
}

const walletSvg = `<svg class="w-6 h-6 text-emerald-600" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
  <path fill-rule="evenodd" d="M12 14a3 3 0 0 1 3-3h4a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-4a3 3 0 0 1-3-3Zm3-1a1 1 0 1 0 0 2h4v-2h-4Z" clip-rule="evenodd"/>
  <path fill-rule="evenodd" d="M12.293 3.293a1 1 0 0 1 1.414 0L16.414 6h-2.828l-1.293-1.293a1 1 0 0 1 0-1.414ZM12.414 6 9.707 3.293a1 1 0 0 0-1.414 0L5.586 6h6.828ZM4.586 7l-.056.055A2 2 0 0 0 3 9v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2h-4a5 5 0 0 1 0-10h4a2 2 0 0 0-1.53-1.945L17.414 7H4.586Z" clip-rule="evenodd"/>
</svg>
`;

const rankingSvg = `<!-- Uploaded to: SVG Repo, www.svgrepo.com, Generator: SVG Repo Mixer Tools -->
<svg class="w-6 h-6 text-orange-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24"><path d="M22,7H16.333V4a1,1,0,0,0-1-1H8.667a1,1,0,0,0-1,1v7H2a1,1,0,0,0-1,1v8a1,1,0,0,0,1,1H22a1,1,0,0,0,1-1V8A1,1,0,0,0,22,7ZM7.667,19H3V13H7.667Zm6.666,0H9.667V5h4.666ZM21,19H16.333V9H21Z"/></svg>`;

const weatherSvg = `
<svg class="w-6 h-6 text-teal-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 582.486 582.486">
<g>
	<g>
		<path d="M385.584,343.002c2.131,0.113,4.275,0.172,6.436,0.172c67.018,0,121.348-54.331,121.348-121.348
			s-54.33-121.347-121.348-121.347c-65.998,0-119.652,52.699-121.271,118.309c24.874,8.538,47.735,22.562,67.015,41.497
			C361.252,283.356,377.58,311.86,385.584,343.002z"/>
		<polygon points="426.107,84.713 392.02,31.359 357.928,84.713 		"/>
		<polygon points="529.133,255.917 582.486,221.829 529.133,187.741 		"/>
		<path d="M254.904,187.741l-33.789,21.588c11.497,0.536,22.797,2.167,33.789,4.826V187.741z"/>
		<polygon points="464.869,100.769 513.076,148.977 526.699,87.146 		"/>
		<polygon points="513.076,294.678 464.869,342.889 526.699,356.508 		"/>
		<polygon points="270.96,148.977 319.168,100.769 257.337,87.146 		"/>
		<path d="M436.545,362.426c-4.504,0-8.928,0.326-13.262,0.938c-17.891,2.521-34.17,10.059-47.371,21.146
			c-0.172-8.707-1.047-17.242-2.531-25.564c-1.09-6.117-2.539-12.104-4.289-17.963c-14.477-48.495-50.836-87.513-97.648-105.601
			c-5.382-2.081-10.899-3.892-16.539-5.4c-13.482-3.605-27.641-5.561-42.265-5.561c-2.268,0-4.517,0.08-6.763,0.172
			c-87.079,3.549-156.592,75.233-156.592,163.181c0,16.686,2.515,32.781,7.163,47.943C25.166,436.412,0,461.938,0,493.393
			c0,31.885,25.851,57.734,57.736,57.734H212.64h7.041h216.864c52.109,0,94.35-42.242,94.35-94.348S488.654,362.426,436.545,362.426
			z"/>
	</g>
</g>
</svg>`;

const tractorSvg = `
<svg class="w-6 h-6 text-yellow-700" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 30 30"><path d="M12.9375 3.9863281 A 1.0001 1.0001 0 0 0 12.828125 4L7 4C5.9069372 4 5 4.9069372 5 6L5 12.767578C3.1744148 14.235915 2 16.483951 2 19 A 1.0001 1.0001 0 0 0 3.0820312 20.009766C3.5754486 23.387457 6.4895795 26 10 26C13.510421 26 16.424551 23.387457 16.917969 20.009766 A 1.0001 1.0001 0 0 0 17.158203 20L20.560547 20C20.213643 20.59163 20 21.269326 20 22C20 24.197294 21.802706 26 24 26C26.197294 26 28 24.197294 28 22C28 20.683604 27.344783 19.51747 26.353516 18.787109L27.957031 13.289062L27.962891 13.267578 A 1 1 0 0 0 27.970703 13.238281 A 1 1 0 0 0 28 13 A 1 1 0 0 0 27 12L22 12L22 7L23 7L23 5L21 5 A 1.0001 1.0001 0 0 0 20 6L20 12L15.78125 12L14.28125 6L15 6 A 1.0001 1.0001 0 1 0 15 4L13.158203 4 A 1.0001 1.0001 0 0 0 13.037109 3.9863281 A 1.0001 1.0001 0 0 0 12.9375 3.9863281 z M 7 6L12.21875 6L13.697266 11.916016C12.589827 11.335011 11.333327 11 10 11C8.9395137 11 7.9275596 11.212321 7 11.589844L7 6 z M 10 14C12.773268 14 15 16.226732 15 19C15 21.773268 12.773268 24 10 24C7.2267316 24 5 21.773268 5 19C5 16.226732 7.2267316 14 10 14 z M 10 17 A 2 2 0 0 0 8 19 A 2 2 0 0 0 10 21 A 2 2 0 0 0 12 19 A 2 2 0 0 0 10 17 z M 24 20C25.116414 20 26 20.883586 26 22C26 23.116414 25.116414 24 24 24C22.883586 24 22 23.116414 22 22C22 20.883586 22.883586 20 24 20 z"/></svg>
`;
